/** Turkish-aware text helpers (İ/ı!). */
export function trUpper(s: string): string {
  return s.toLocaleUpperCase('tr-TR');
}

export function trLower(s: string): string {
  return s.toLocaleLowerCase('tr-TR');
}

/** "Mert Aslan" → "MA", "ilker" → "İL" */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return trUpper(parts[0]!.slice(0, 2));
  return trUpper(parts[0]!.charAt(0) + parts[1]!.charAt(0));
}

/** Search normalisation: case- and diacritic-insensitive for Turkish. */
export function searchKey(s: string): string {
  return trLower(s)
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * The fold `items.search_key` is generated with — a mirror of `private.tr_fold`
 * (migration 20260923000001). A term typed into the product search must be folded
 * the same way before it is sent as an `ilike` pattern, or "Fildişi" would never
 * match the stored "fildisi". Deliberately different from `searchKey()`: this one
 * folds `×` to `x` and the circumflex vowels, and leaves everything else alone.
 */
export function trFold(s: string | null | undefined): string {
  const from = 'ÇĞİIÖŞÜÂÎÛçğıöşüâîû×';
  const to = 'CGIIOSUAIUcgiosuaiux';
  let out = '';
  for (const ch of s ?? '') {
    const i = from.indexOf(ch);
    out += i === -1 ? ch : to[i];
  }
  return out.toLowerCase();
}

/**
 * Warehouse / location / work-centre code normaliser: "d1 a/02" → "D1-A-02".
 * Mirrors `private.code_key`. Turkish letters fold to ASCII (so `i` never becomes
 * `İ`), every other run of non-alphanumerics becomes a single hyphen, and the
 * result is trimmed of leading and trailing hyphens. Empty input gives "".
 */
export function codeKey(s: string | null | undefined): string {
  const from = 'çğıöşüÇĞİÖŞÜ';
  const to = 'cgiosuCGIOSU';
  let folded = '';
  for (const ch of s ?? '') {
    const i = from.indexOf(ch);
    folded += i === -1 ? ch : to[i];
  }
  return folded
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toUpperCase();
}

export function matchesSearch(haystack: (string | null | undefined)[], needle: string): boolean {
  const n = searchKey(needle.trim());
  if (!n) return true;
  return haystack.some((h) => h && searchKey(h).includes(n));
}

/** Validates a Turkish VKN (10 digits) with its checksum. */
export function isValidVkn(vkn: string): boolean {
  if (!/^\d{10}$/.test(vkn)) return false;
  const d = vkn.split('').map(Number);
  let s = 0;
  for (let i = 0; i < 9; i++) {
    const t = (d[i]! + 10 - (i + 1)) % 10;
    let p = (t * 2 ** (10 - (i + 1))) % 9;
    if (t !== 0 && p === 0) p = 9;
    s += p;
  }
  return (10 - (s % 10)) % 10 === d[9];
}

/** Validates a Turkish TCKN (11 digits) with its checksums. */
export function isValidTckn(tckn: string): boolean {
  if (!/^[1-9]\d{10}$/.test(tckn)) return false;
  const d = tckn.split('').map(Number);
  const odd = d[0]! + d[2]! + d[4]! + d[6]! + d[8]!;
  const even = d[1]! + d[3]! + d[5]! + d[7]!;
  const d10 = (((odd * 7 - even) % 10) + 10) % 10;
  const d11 = (d.slice(0, 10).reduce((a, b) => a + b, 0)) % 10;
  return d10 === d[9] && d11 === d[10];
}

export function isValidTaxNo(v: string): boolean {
  return isValidVkn(v) || isValidTckn(v);
}

/** Normalises and validates an IBAN (mod 97). */
export function isValidIban(iban: string): boolean {
  const s = iban.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s)) return false;
  if (s.startsWith('TR') && s.length !== 26) return false;
  const rearranged = s.slice(4) + s.slice(0, 4);
  let rem = 0;
  for (const ch of rearranged) {
    const v = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of v) rem = (rem * 10 + Number(digit)) % 97;
  }
  return rem === 1;
}

export function formatIban(iban: string): string {
  return iban.replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();
}

/** "Aslen Kağıtçılık San. ve Tic. Ltd. Şti." → "Aslen Kağıtçılık" (top bar / short name). */
export function shortCompanyName(legal: string): string {
  const s = legal
    .replace(/\s+(San\.?\s*(ve\s*)?Tic\.?|Sanayi\s+ve\s+Ticaret|Tic\.?)\b.*$/i, '')
    .replace(/\s+(Ltd\.?\s*Şti\.?|Limited\s+Şirketi|A\.?\s*Ş\.?|Anonim\s+Şirketi)\s*$/i, '')
    .trim();
  return s || legal.trim();
}

/**
 * The name a stored document should be shown with. objectPath() puts the bucket
 * folders and a uuid in front of the file ("<company>/invoices/<uuid>-fatura.pdf"),
 * and none of that belongs on screen.
 */
export function attachmentName(path: string | null | undefined): string {
  const file = (path ?? '').split('/').pop() ?? '';
  return file.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, '');
}

/** The typographic apostrophe Turkish suffixes are attached with (U+2019). */
export const APOSTROPHE = '’';

const BACK_UNROUNDED = 'aı';
const BACK_ROUNDED = 'ou';
const FRONT_ROUNDED = 'öü';
const VOWELS = 'aeıioöuü';

/**
 * Turkish genitive of a person's name — "Serkan’ın sayımı onayını bekliyor"
 * (W-09 banner). The suffix follows the last vowel (a/ı → ’ın, o/u → ’un,
 * e/i → ’in, ö/ü → ’ün) and a name that ends in a vowel takes the buffer "n"
 * ("Ayşe’nin"). A proper noun keeps its spelling, so no consonant softening.
 *
 * A name with no vowel at all (initials, a code) falls back to "’in", which is
 * what Turkish does with letters read out loud ("X’in").
 */
export function trGenitive(name: string | null | undefined): string {
  const s = (name ?? '').trim();
  if (!s) return '';
  const lower = trLower(s);
  let vowel = '';
  for (let i = lower.length - 1; i >= 0; i--) {
    if (VOWELS.includes(lower[i]!)) {
      vowel = lower[i]!;
      break;
    }
  }
  const suffix = BACK_UNROUNDED.includes(vowel) ? 'ın' : BACK_ROUNDED.includes(vowel) ? 'un' : FRONT_ROUNDED.includes(vowel) ? 'ün' : 'in';
  const buffer = VOWELS.includes(lower[lower.length - 1]!) ? 'n' : '';
  return `${s}${APOSTROPHE}${buffer}${suffix}`;
}
